import { useEffect, useRef, useState } from 'react';

import { datadogRum } from '@datadog/browser-rum';
import { IconInfoCircle, IconX } from '@tabler/icons-react';
import { toast } from 'react-hot-toast';
import { useTranslation } from 'react-i18next';

import { AmplitudeProductEvent } from '@cfa/pathway-fe-shared';
import { PathwayUser, Plan } from '@cfa/pathway-fe-shared/xp';
import {
  Button,
  Checkbox,
  TextFieldType,
  Typography,
} from '@cfa/react-components';
import { Focusable, MessageBlock, Tooltip } from '@cfa/react-core';

import useAuth from '@/hooks/useAuth';
import { usePlanLocations } from '@/hooks/usePlanLocations';

import { itemByLanguage } from '@/utils/language';
import { cn } from '@/utils/tailwind';

import LocationsFilter from '@/components/LocationsFilter/LocationsFilter';
import PathwayModal from '@/components/popups/PathwayModal';
import Searchbar from '@/components/Searchbar/Searchbar';
import ToastMessageBlock from '@/components/Toasts/SuccessToast';

import Constants from '@/constants';

import AutoAssignToggle from './AutoAssignToggle';
import TeamMemberCard from './TeamMemberCard';
import { usePatchAutoAssign } from './usePatchAutoAssign';
import { useTeamMemberData } from './useTeamMemberData';
import { useModalDurationTracking } from './utils';

import { useAmplitudeTrack } from '@/amplitude/useAmplitude';
import { AlreadyAssignedXsm, CompletedXsm } from '@/icons';

export interface TeamMembersStepProps {
  isOpen: boolean;
  onClose: () => void;
  plan?: Plan;
  isCompliance?: boolean;
  isTrainTogetherMode?: boolean;
  reassignEligible?: boolean;
  teamMemberQueryOptions?: Record<string, unknown>;
  onNext: (selectedTeamMembers: PathwayUser[]) => void;
}

const TeamMembersStep: React.FC<TeamMembersStepProps> = ({
  isOpen,
  onClose,
  plan,
  isCompliance,
  isTrainTogetherMode,
  reassignEligible = false,
  teamMemberQueryOptions,
  onNext,
}) => {
  const { t } = useTranslation();
  const track = useAmplitudeTrack();
  const searchInputRef = useRef<HTMLInputElement>(null);

  const patchAutoAssign = usePatchAutoAssign();

  const amplitudeEventProperties = {
    plan_id: plan?.id,
    plan_name: itemByLanguage(plan?.name),
    is_compliance: isCompliance ?? false,
  };

  const planAutoAssign = !!plan?.autoAssign;
  const [autoAssignToggle, setAutoAssignToggle] = useState(planAutoAssign);

  useEffect(() => {
    setAutoAssignToggle(isCompliance ? true : planAutoAssign);
  }, [planAutoAssign, isCompliance]);

  const hasUnsavedAutoAssignChange =
    !isCompliance && autoAssignToggle !== planAutoAssign;

  useModalDurationTracking(isOpen, amplitudeEventProperties);

  const [searchTermDisplayed, setSearchTermDisplayed] = useState('');
  const [selectedTeamMembers, setSelectedTeamMembers] = useState<PathwayUser[]>(
    [],
  );
  const [filterBy, setFilterBy] = useState<string[]>();
  const [isSelectAll, setIsSelectAll] = useState(false);
  const [showBanner, setShowBanner] = useState(
    () => localStorage.getItem('reassignment-banner-dismissed') !== 'true',
  );
  const [hidePreviouslyAssigned, setHidePreviouslyAssigned] = useState(
    isCompliance ? true : false,
  );
  const [isInfoTooltipOpen, setIsInfoTooltipOpen] = useState(false);

  const { data: auth } = useAuth();
  const canShowAutoAssign =
    !isTrainTogetherMode && auth?.isLicensee === false && !!plan?.id;
  const {
    getLocationsByPlan: getLocationsByPlanUnsafe,
    isReady: isLocationsReadyUnsafe,
  } = usePlanLocations();

  const getLocationsByPlan = getLocationsByPlanUnsafe as (
    plan: Plan,
  ) => string[];
  const isLocationsReady = isLocationsReadyUnsafe;

  const locations: string[] = plan
    ? getLocationsByPlan(plan)
    : isCompliance
      ? (auth?.assignableTrainerLocations ?? [])
      : [];

  const {
    assignments,
    currentCount,
    filteredTeamMembers,
    selectableTeamMembers,
    alreadyAssignedTeamMembers,
    getTeamMemberPlanStatus,
    getCompletionDate,
  } = useTeamMemberData({
    isOpen,
    locations,
    plan,
    isCompliance,
    isTrainTogetherMode,
    searchTerm: searchTermDisplayed,
    filterBy,
    hidePreviouslyAssigned,
    selectedTeamMembers,
    teamMemberQueryOptions,
  });

  const showReassignmentFeatures = !isTrainTogetherMode && reassignEligible;

  const wasOpenRef = useRef(false);

  useEffect(() => {
    if (isOpen) {
      wasOpenRef.current = true;
    } else if (wasOpenRef.current && showBanner && showReassignmentFeatures) {
      localStorage.setItem('reassignment-banner-dismissed', 'true');
      setShowBanner(false);
      wasOpenRef.current = false;
    }
  }, [isOpen, showBanner, showReassignmentFeatures]);

  const onClearSearchClick = () => {
    setSearchTermDisplayed('');
    searchInputRef.current?.focus();
  };

  const onSearchInputChange = (e?: React.ChangeEvent<TextFieldType>) => {
    setIsSelectAll(false);
    setSearchTermDisplayed(e?.target.value ?? '');
  };

  const onAssignTeamMember = (member: PathwayUser) => {
    setIsSelectAll(false);
    const isSelected = selectedTeamMembers.find(
      selectedTeamMember => selectedTeamMember.adId === member.adId,
    );
    if (isSelected) {
      setSelectedTeamMembers(
        selectedTeamMembers.filter(
          selectedTeamMember => selectedTeamMember.adId !== member.adId,
        ),
      );
    } else {
      setSelectedTeamMembers([...selectedTeamMembers, member]);
    }
  };

  const onAssignAllTeamMembers = () => {
    const combinedSelectedTeamMembers = [
      ...new Set([...selectedTeamMembers, ...(selectableTeamMembers ?? [])]),
    ];
    setIsSelectAll(prev => !prev);
    setSelectedTeamMembers(!isSelectAll ? combinedSelectedTeamMembers : []);
  };

  const onChipClick = (location: string) => {
    if (filterBy?.includes(location)) {
      setFilterBy(filterBy.filter(it => it !== location));
    } else {
      setFilterBy(filter => [...(filter ?? []), location]);
    }
  };

  const resetCount = () => {
    setSelectedTeamMembers([]);
  };

  const onNextClick = async () => {
    // Persist auto-assign here whenever the toggle differs from the plan —
    // including when team members are also selected; parents only handle
    // the selected members.
    if (hasUnsavedAutoAssignChange) {
      const planId = plan?.id;
      if (!planId) {
        datadogRum.addError(
          new Error('[AddTeamMembersPopUp] No plan ID found'),
        );
        return;
      }
      try {
        await patchAutoAssign.mutateAsync({
          path: { planId },
          body: {
            autoAssign: autoAssignToggle,
            ...(autoAssignToggle ? { dueDateOffsetInDays: 14 } : {}),
          },
        });
        if (autoAssignToggle) {
          track(
            AmplitudeProductEvent.AUTO_ASSIGN_ENABLED,
            amplitudeEventProperties,
          );
        }
      } catch (err) {
        toast.custom(toastObj => (
          <ToastMessageBlock id={toastObj.id} severity="error">
            {err instanceof Error
              ? err.message
              : t('TrainingPlans.toastMessage.assignmentError')}
          </ToastMessageBlock>
        ));
        if (selectedTeamMembers.length === 0) {
          onClose();
        }
        return;
      }

      if (selectedTeamMembers.length === 0) {
        toast.custom(toastObj => (
          <ToastMessageBlock id={toastObj.id} severity="success">
            {t('autoAssignToggle.assignmentUpdated')}
          </ToastMessageBlock>
        ));
        onClose();
        return;
      }
    }

    if (selectedTeamMembers.length > 0) {
      track(AmplitudeProductEvent.TEAM_MEMBERS_ASSIGNED, {
        ...amplitudeEventProperties,
        num_team_members: selectedTeamMembers.length,
        total_selectable_team_members: selectableTeamMembers?.length ?? 0,
        is_train_together_mode: !!isTrainTogetherMode,
        auto_assign_also_enabled: autoAssignToggle,
      });

      onNext(selectedTeamMembers);

      if (!isTrainTogetherMode) {
        resetCount();
      }
    }
  };

  const addTMCountText = t('Button.addXObjects', {
    count: currentCount,
    object:
      currentCount === 1 ? t('Generic.teamMember') : t('Generic.teamMembers'),
  });

  return (
    <>
      <PathwayModal.Header>
        <PathwayModal.Title>
          {t('Generic.chooseTeamMembers')}
        </PathwayModal.Title>
        {showReassignmentFeatures && showBanner && (
          <MessageBlock.Root severity="info" className="mt-6! text-start!">
            <div className="flex w-full items-start justify-between">
              <div>
                <Typography variant="body1" className="font-semibold!">
                  {t('TrainingPlans.teamMembers.recertificationBannerTitle')}
                </Typography>
                <Typography variant="body2" className="mt-1!">
                  {t('TrainingPlans.teamMembers.recertificationBannerBody')}
                </Typography>
              </div>
              <button
                onClick={() => {
                  setShowBanner(false);
                  localStorage.setItem('reassignment-banner-dismissed', 'true');
                }}
                className="ml-2 shrink-0 p-1"
                aria-label="Dismiss"
              >
                <IconX size={20} className="text-grayscale-gray5" />
              </button>
            </div>
          </MessageBlock.Root>
        )}
        {canShowAutoAssign && (
          <AutoAssignToggle
            isSelected={autoAssignToggle}
            onChange={setAutoAssignToggle}
            isCompliance={isCompliance}
          />
        )}
        <Searchbar
          elevation={1}
          onChange={onSearchInputChange}
          onClear={onClearSearchClick}
          placeholder={t('TrainingPlans.teamMembers.searchbarPlaceholder')}
          ref={searchInputRef}
          searchValue={searchTermDisplayed}
          className={cn('z-5! mt-6! mb-2!', {
            'mt-0!': canShowAutoAssign,
          })}
        />
      </PathwayModal.Header>
      <PathwayModal.Body noMinHeight>
        <div>
          {showReassignmentFeatures && !isCompliance && (
            <div className="mb-4 flex items-start gap-4 sm:gap-2">
              <Checkbox
                checked={hidePreviouslyAssigned}
                onChange={() => setHidePreviouslyAssigned(prev => !prev)}
                label={t('TrainingPlans.teamMembers.hideFilter')}
                className="[&_label]:text-md! [&_label]:items-start! [&_label]:text-left! max-sm:[&_label]:text-sm!"
              />
              <Tooltip.Root
                delay={200}
                closeDelay={100}
                isOpen={isInfoTooltipOpen}
                onOpenChange={setIsInfoTooltipOpen}
              >
                <Focusable>
                  <IconInfoCircle
                    size={24}
                    className="text-grayscale-gray5 mt-[2px] shrink-0 cursor-pointer"
                    onClick={() => setIsInfoTooltipOpen(prev => !prev)}
                  />
                </Focusable>
                <Tooltip.Content
                  className="max-w-xs! px-6! py-4!"
                  placement="bottom start"
                >
                  <p className="text-base font-normal">
                    {t(
                      'TrainingPlans.teamMembers.tooltipIntro',
                      'A Team Member who',
                    )}
                  </p>
                  <div className="mt-2 flex items-start gap-2">
                    <CompletedXsm className="mt-0.5 size-5 shrink-0" />
                    <span className="text-base font-normal">
                      {t(
                        'TrainingPlans.teamMembers.tooltipCompletedDescription',
                      )}
                    </span>
                  </div>
                  <div className="mt-2 flex items-start gap-2">
                    <AlreadyAssignedXsm className="mt-0.5 size-5 shrink-0" />
                    <span className="text-base font-normal">
                      {t(
                        'TrainingPlans.teamMembers.tooltipAssignedDescription',
                      )}
                    </span>
                  </div>
                </Tooltip.Content>
              </Tooltip.Root>
            </div>
          )}

          {isLocationsReady && locations.length > 1 && (
            <LocationsFilter
              locations={locations}
              onClick={location => onChipClick(location)}
              onClose={location => onChipClick(location)}
              selectedLocations={filterBy}
            />
          )}
          <div className="mb-2 flex items-center justify-end">
            <Button
              color="secondary"
              disabled={
                isTrainTogetherMode
                  ? false
                  : selectableTeamMembers?.length === 0
              }
              onClick={onAssignAllTeamMembers}
              size="sm"
              variant="text"
              className="disabled:bg-transparent!"
            >
              <span className="text-grayscale-gray6 text-16 font-normal underline">
                {!isSelectAll ? t('Button.selectAll') : t('Button.clearAll')}
              </span>
            </Button>
          </div>

          {!!filteredTeamMembers?.length &&
            filteredTeamMembers
              .toSorted((a, b) =>
                a.name === b.name
                  ? 0
                  : (a.firstName ?? '').localeCompare(b.firstName ?? '') ||
                    (a.lastName ?? '').localeCompare(b.lastName ?? ''),
              )
              .map(member => {
                const memberAdId = member.adId;
                if (!memberAdId) {
                  return null;
                }
                return (
                  <TeamMemberCard
                    noHover
                    id={memberAdId}
                    isAssigned={
                      !!selectedTeamMembers.find(
                        selectedTeamMember =>
                          selectedTeamMember.adId === memberAdId,
                      )
                    }
                    isTrainTogetherMode={!!isTrainTogetherMode}
                    key={memberAdId}
                    name={`${member.prefName ?? member.firstName} ${member.lastName}`}
                    onAssignTeamMember={() => onAssignTeamMember(member)}
                    originallyAssigned={
                      isTrainTogetherMode
                        ? (assignments
                            ?.filter(
                              teamMember =>
                                teamMember.status ===
                                Constants.TRAINING_PLANS.COMPLETED,
                            )
                            .map(it => it.userId)
                            .includes(member.adId) ?? false)
                        : !showReassignmentFeatures &&
                          (alreadyAssignedTeamMembers?.includes(member.adId) ??
                            false)
                    }
                    reassignEligible={reassignEligible}
                    displayName={`${member.prefName ?? member.firstName} ${member.lastName}`}
                    planStatus={getTeamMemberPlanStatus(memberAdId)}
                    completionDate={getCompletionDate(memberAdId)}
                  />
                );
              })}

          {isCompliance && filteredTeamMembers?.length === 0 && (
            <Typography variant="body1" className="text-left">
              {t('Search.allTeamMembersAssigned')}
            </Typography>
          )}
        </div>
      </PathwayModal.Body>
      <PathwayModal.Footer>
        <PathwayModal.PrimaryButton
          color="secondary"
          testId="ButtonNext"
          isDisabled={
            canShowAutoAssign
              ? (!hasUnsavedAutoAssignChange && currentCount === 0) ||
                patchAutoAssign.isPending
              : currentCount === 0
          }
          onPress={onNextClick}
        >
          {canShowAutoAssign && hasUnsavedAutoAssignChange && currentCount === 0
            ? t('Button.continue')
            : addTMCountText}
        </PathwayModal.PrimaryButton>
      </PathwayModal.Footer>
    </>
  );
};

export default TeamMembersStep;
